import React from "react";
import { useTranslation } from "react-i18next";
import "./offer-workspace.css";

/**
 * Live PDF preview pane for Dokument-tab — iframe of absolute pdf_url from
 * GET /offers/:id/pdf_offer?layout=document. Keeps the previous PDF visible
 * while a refresh is in flight.
 */
export default function OfferDocumentLivePreview({
  pdfUrl,
  loading = false,
  dirty = false,
  error = null,
  onRefresh,
}) {
  const { t } = useTranslation();
  const showEmpty = !pdfUrl && !loading;

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col bg-slate-100/60">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white/90 px-4 py-2">
        {dirty ? (
          <span
            className="offer-save-chip is-busy"
            role="status"
            aria-live="polite"
          >
            {t("offers.documentBuilder.preview.showing_saved")}
          </span>
        ) : null}
        {onRefresh ? (
          <button
            type="button"
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onRefresh}
            disabled={loading}
          >
            {t("offers.documentBuilder.preview.refresh")}
          </button>
        ) : null}
        {loading ? (
          <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-500" role="status">
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#1463C7] border-t-transparent"
              aria-hidden="true"
            />
            {t("offers.documentBuilder.preview.loading")}
          </span>
        ) : null}
      </div>

      <div className="relative min-h-[480px] flex-1 lg:min-h-0">
        {pdfUrl ? (
          <iframe
            key={pdfUrl}
            title={t("offers.documentBuilder.preview.label")}
            src={pdfUrl}
            className="absolute inset-0 h-full w-full border-0 bg-white"
          />
        ) : null}

        {loading && !pdfUrl ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-[#1463C7] border-t-transparent"
              role="status"
              aria-label={t("offers.documentBuilder.preview.loading")}
            />
          </div>
        ) : null}

        {loading && pdfUrl ? (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-3"
            aria-hidden="true"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm ring-1 ring-slate-200">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#1463C7] border-t-transparent" />
              {t("offers.documentBuilder.preview.loading")}
            </span>
          </div>
        ) : null}

        {showEmpty && error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-sm font-medium text-slate-700">{error}</p>
            {onRefresh ? (
              <button
                type="button"
                className="rounded-md bg-[#1463C7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0B3A78]"
                onClick={onRefresh}
              >
                {t("offers.documentBuilder.preview.refresh")}
              </button>
            ) : null}
          </div>
        ) : null}

        {showEmpty && !error ? (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <p className="text-sm text-slate-500">
              {t("offers.documentBuilder.preview.empty_live")}
            </p>
          </div>
        ) : null}

        {error && pdfUrl ? (
          <div className="absolute bottom-3 left-1/2 z-10 max-w-md -translate-x-1/2 rounded-md bg-rose-50 px-3 py-2 text-center text-xs font-medium text-rose-700 ring-1 ring-rose-200">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
